import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiCheck, FiAlertCircle, FiZap } from 'react-icons/fi';
import Layout from '../components/Layout';
import theme from '../styles/theme';
import { planosAPI } from '../services/api';
import { contratosAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 1200px;
  margin: 0 auto;
  padding: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[600]};
    background: ${theme.colors.primary[50]};
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['3xl']};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.text.muted};
  margin: 0;
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing[6]};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.div`
  background: ${props => props.$isCurrent 
    ? `linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.primary[100]} 100%)` 
    : theme.colors.background.secondary};
  border: 3px solid ${props => props.$isCurrent ? theme.colors.primary[500] : theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  position: relative;
  transition: all 0.3s ease;
  box-shadow: ${props => props.$isCurrent 
    ? `0 4px 16px ${theme.colors.primary[200]}80` 
    : 'none'};

  ${props => props.$isCurrent && `
    transform: scale(1.02);
  `}

  ${props => !props.$isCurrent && !props.$isDisabled && `
    &:hover {
      border-color: ${theme.colors.primary[500]};
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
      transform: translateY(-4px);
    }
  `}

  ${props => props.$isDisabled && !props.$isCurrent && `
    opacity: 0.5;
    cursor: not-allowed;
  `}
`;

const PlanHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
`;

const PlanName = styled.h2`
  font-size: ${props => props.$isCurrent ? theme.fontSizes['3xl'] : theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${props => props.$isCurrent ? theme.colors.primary[700] : theme.colors.text.primary};
  margin: 0;
`;

const ActiveBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${theme.colors.success[500]};
  color: white;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: 600;
`;

const PlanDescription = styled.p`
  font-size: ${props => props.$isCurrent ? theme.fontSizes.base : theme.fontSizes.sm};
  color: ${props => props.$isCurrent ? '#1e293b' : theme.colors.text.muted};
  line-height: 1.6;
  margin: 0;
  font-weight: ${props => props.$isCurrent ? '500' : 'normal'};
`;

const PlanDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const PlanResources = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[2]};
`;

const ResourcesTitle = styled.h4`
  font-size: ${props => props.$isCurrent ? theme.fontSizes.base : theme.fontSizes.sm};
  font-weight: 600;
  color: ${props => props.$isCurrent ? '#0f172a' : theme.colors.text.primary};
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const ResourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  max-height: none;
  overflow-y: visible;
`;

const ResourceItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]};
  background: ${props => props.$isCurrent 
    ? theme.colors.primary[50] 
    : theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
  border-left: 3px solid ${props => props.$isCurrent 
    ? theme.colors.primary[600] 
    : theme.colors.primary[300]};
`;

const ResourceIcon = styled.div`
  color: ${props => props.$isCurrent 
    ? theme.colors.primary[700] 
    : theme.colors.primary[500]};
  flex-shrink: 0;
  margin-top: 2px;
`;

const ResourceContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const ResourceDescription = styled.span`
  font-size: ${props => props.$isCurrent ? theme.fontSizes.base : theme.fontSizes.sm};
  color: ${props => props.$isCurrent 
    ? '#1e293b' 
    : theme.colors.text.secondary};
  line-height: 1.4;
  font-weight: ${props => props.$isCurrent ? '500' : 'normal'};
`;

const InheritedResourcesNote = styled.div`
  padding: ${theme.spacing[3]};
  background: ${props => props.$isCurrent 
    ? theme.colors.primary[200] 
    : theme.colors.gray[800]};
  border-radius: ${theme.borderRadius.lg};
  border-left: 3px solid ${props => props.$isCurrent 
    ? theme.colors.primary[600] 
    : theme.colors.gray[500]};
  margin-bottom: ${theme.spacing[2]};
  font-size: ${props => props.$isCurrent ? theme.fontSizes.sm : theme.fontSizes.sm};
  color: ${props => props.$isCurrent 
    ? '#0f172a' 
    : theme.colors.text.primary};
  font-weight: 600;
  font-style: italic;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${props => props.$isCurrent ? theme.fontSizes.base : theme.fontSizes.sm};
  color: ${props => props.$isCurrent ? '#1e293b' : theme.colors.text.secondary};
`;

const DetailLabel = styled.span`
  font-weight: 600;
  min-width: 140px;
`;

const DetailValue = styled.span`
  color: ${props => props.$isCurrent ? '#0f172a' : theme.colors.text.primary};
  font-weight: 700;
  font-size: ${props => props.$isCurrent ? theme.fontSizes.lg : 'inherit'};
`;

const UpgradeButton = styled.button`
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  border-radius: ${theme.borderRadius.xl};
  border: 2px solid ${props => props.$isCurrent ? theme.colors.primary[400] : 'transparent'};
  background: ${props => props.$isCurrent 
    ? `linear-gradient(135deg, ${theme.colors.primary[400]} 0%, ${theme.colors.primary[500]} 100%)` 
    : theme.colors.primary[500]};
  color: white;
  font-weight: ${props => props.$isCurrent ? '700' : '600'};
  font-size: ${props => props.$isCurrent ? theme.fontSizes.lg : theme.fontSizes.base};
  cursor: ${props => props.$isCurrent || props.$isDisabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;
  margin-top: auto;
  box-shadow: ${props => props.$isCurrent 
    ? `0 2px 8px ${theme.colors.primary[300]}60` 
    : 'none'};

  ${props => !props.$isCurrent && !props.$isDisabled && `
    &:hover {
      background: ${theme.colors.primary[600]};
    }
  `}

  ${props => props.$isCurrent && `
    &:active {
      transform: none;
    }
  `}
`;

const CardInfoSection = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const CardInfoHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const CardInfoTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const CardInfoContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.background.primary};
  border-radius: ${theme.borderRadius.lg};
`;

const CardInfoDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const CardNumber = styled.div`
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  color: ${theme.colors.text.primary};
`;

const CardHolder = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.muted};
`;

const AlertBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.warning[50]};
  border: 1px solid ${theme.colors.warning[300]};
  border-radius: ${theme.borderRadius.xl};
  color: ${theme.colors.warning[800]};
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.muted};
`;


const UpgradePlano = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clienteUuidParam = searchParams.get('cliente_uuid');
  const clienteIdParam = searchParams.get('cliente_id'); // Manter compatibilidade
  const { isGestor } = useAuth();

  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);

  // Priorizar UUID, mas manter compatibilidade com ID
  const clienteUuid = clienteUuidParam || null;
  const clienteId = clienteIdParam ? parseInt(clienteIdParam, 10) : null;

  useEffect(() => {
    if (!clienteUuid && !clienteId) {
      // Se não houver cliente_uuid ou cliente_id, buscar do dashboard
      carregarDados();
    } else {
      carregarDados(clienteUuid, clienteId);
    }
  }, [clienteUuid, clienteId]);

  const carregarDados = async (clienteUuidParam = null, clienteIdParam = null) => {
    try {
      setLoading(true);

      // Se não houver cliente_uuid ou cliente_id, buscar do dashboard para obter o cliente
      let clienteUuidParaBuscar = clienteUuidParam;
      let clienteIdParaBuscar = clienteIdParam;
      
      if (!clienteUuidParaBuscar && !clienteIdParaBuscar) {
        const dashboardResponse = await contratosAPI.dashboardMeusContratos();
        if (dashboardResponse.data?.success && dashboardResponse.data.data?.clientes?.length > 0) {
          const primeiroCliente = dashboardResponse.data.data.clientes[0];
          clienteUuidParaBuscar = primeiroCliente.uuid;
          clienteIdParaBuscar = primeiroCliente.id;
        } else {
          toast.error('Nenhum cliente encontrado');
          navigate('/dashboard/creditos');
          return;
        }
      }

      // Usar UUID se disponível, senão usar ID
      const response = clienteUuidParaBuscar
        ? await planosAPI.listarPlanosUpgrade(null, clienteUuidParaBuscar)
        : await planosAPI.listarPlanosUpgrade(clienteIdParaBuscar);
      
      if (response.data?.success) {
        setDados(response.data.data);
      } else {
        toast.error('Erro ao carregar planos disponíveis');
        navigate('/dashboard/creditos');
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error(error.response?.data?.error || 'Erro ao carregar planos disponíveis');
      navigate('/dashboard/creditos');
    } finally {
      setLoading(false);
    }
  };

  const abrirTelaSelecaoCartao = async (planoId) => {
    try {
      console.log('[UpgradePlano] Iniciando seleção de cartão', { planoId, clienteUuid, clienteId });
      
      if (!isGestor()) {
        toast.error('Apenas gestores podem fazer upgrade de plano');
        return;
      }

      // Armazenar plano_id temporariamente no sessionStorage para segurança
      sessionStorage.setItem('upgrade_plano_id', planoId.toString());
      console.log('[UpgradePlano] Plano ID armazenado no sessionStorage:', planoId);

      // Redirecionar para página de seleção de cartão apenas com cliente_uuid
      if (clienteUuid) {
        console.log('[UpgradePlano] Redirecionando com cliente_uuid:', clienteUuid);
        navigate(`/selecionar-cartao-upgrade?cliente_uuid=${clienteUuid}`);
      } else if (clienteId) {
        // Se não tiver UUID, buscar do dashboard
        console.log('[UpgradePlano] Buscando UUID do cliente pelo ID:', clienteId);
        try {
          const id = await buscarClienteIdDoDashboard();
          if (id) {
            // Buscar UUID do cliente pelo ID
            try {
              const clienteResponse = await contratosAPI.dashboardMeusContratos();
              console.log('[UpgradePlano] Resposta do dashboard:', { 
                success: clienteResponse?.data?.success,
                clientesCount: clienteResponse?.data?.data?.clientes?.length || 0
              });
              
              if (clienteResponse?.data?.success && clienteResponse?.data?.data?.clientes?.length > 0) {
                const cliente = clienteResponse.data.data.clientes.find(c => c.id === id);
                if (cliente && cliente.uuid) {
                  console.log('[UpgradePlano] Cliente encontrado, redirecionando com UUID:', cliente.uuid);
                  navigate(`/selecionar-cartao-upgrade?cliente_uuid=${cliente.uuid}`);
                } else {
                  console.error('[UpgradePlano] Cliente não encontrado ou sem UUID:', { id, cliente });
                  toast.error('Erro ao identificar cliente');
                }
              } else {
                console.error('[UpgradePlano] Resposta inválida do dashboard');
                toast.error('Erro ao identificar cliente');
              }
            } catch (error) {
              console.error('[UpgradePlano] Erro ao buscar cliente do dashboard:', error);
              toast.error('Erro ao identificar cliente');
            }
          } else {
            console.error('[UpgradePlano] ID do cliente não encontrado');
            toast.error('Cliente não identificado');
          }
        } catch (error) {
          console.error('[UpgradePlano] Erro ao buscar cliente ID:', error);
          toast.error('Erro ao identificar cliente');
        }
      } else {
        console.error('[UpgradePlano] Cliente não identificado');
        toast.error('Cliente não identificado');
      }
    } catch (error) {
      console.error('[UpgradePlano] Erro inesperado ao abrir seleção de cartão:', error);
      toast.error('Erro ao abrir seleção de cartão');
    }
  };

  const buscarClienteIdDoDashboard = async () => {
    try {
      const dashboardResponse = await contratosAPI.dashboardMeusContratos();
      if (dashboardResponse.data?.success && dashboardResponse.data.data?.clientes?.length > 0) {
        return dashboardResponse.data.data.clientes[0].id;
      }
    } catch (error) {
      console.error('Erro ao buscar cliente:', error);
    }
    return null;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };


  if (loading) {
    return (
      <Layout>
        <Container>
          <LoadingSpinner>Carregando planos disponíveis...</LoadingSpinner>
        </Container>
      </Layout>
    );
  }

  if (!dados) {
    return (
      <Layout>
        <Container>
          <AlertBox>
            <FiAlertCircle size={24} />
            <div>
              <strong>Erro ao carregar dados</strong>
              <p>Não foi possível carregar os planos disponíveis.</p>
            </div>
          </AlertBox>
        </Container>
      </Layout>
    );
  }

  const { planoAtual, planosDisponiveis } = dados;
  const todosPlanos = [planoAtual, ...planosDisponiveis].filter(Boolean);

  // Função para calcular recursos novos de um plano comparado ao anterior
  const calcularRecursosNovos = (plano, todosPlanosOrdenados) => {
    if (!plano.recursos || plano.recursos.length === 0) {
      return { recursosNovos: [], planoAnteriorNome: null };
    }

    // Ordenar todos os planos por valor
    const planosOrdenados = [...todosPlanosOrdenados].sort((a, b) => 
      Number.parseFloat(a.valor || 0) - Number.parseFloat(b.valor || 0)
    );

    // Encontrar o índice do plano atual na lista ordenada
    const indicePlanoAtual = planosOrdenados.findIndex(p => 
      (p.id && plano.id && p.id === plano.id) || 
      (!p.id && !plano.id && p.nome === plano.nome)
    );

    // Se for o primeiro plano ou não encontrou, mostrar todos os recursos
    if (indicePlanoAtual <= 0) {
      return { recursosNovos: plano.recursos, planoAnteriorNome: null };
    }

    // Buscar plano anterior
    const planoAnterior = planosOrdenados[indicePlanoAtual - 1];
    if (!planoAnterior || !planoAnterior.recursos || planoAnterior.recursos.length === 0) {
      return { recursosNovos: plano.recursos, planoAnteriorNome: null };
    }

    // Identificar IDs dos recursos do plano anterior
    const recursosAnterioresIds = new Set(
      planoAnterior.recursos.map(r => r.id).filter(Boolean)
    );

    // Filtrar apenas recursos novos (que não estão no plano anterior)
    const recursosNovos = plano.recursos.filter(
      recurso => !recursosAnterioresIds.has(recurso.id)
    );

    return {
      recursosNovos,
      planoAnteriorNome: planoAnterior.nome,
    };
  };

  return (
    <Layout>
      <Container>
        <Header>
          <BackButton onClick={() => navigate('/dashboard/creditos')}>
            <FiArrowLeft />
          </BackButton>
          <HeaderContent>
            <Title>Upgrade de Plano</Title>
            <Subtitle>Escolha um plano superior para seu contrato</Subtitle>
          </HeaderContent>
        </Header>


        <PlansGrid>
          {todosPlanos.map((plano, index) => {
            const isCurrent = plano.id === planoAtual.id || 
                             (plano.id === undefined && planoAtual.id === undefined && plano.nome === planoAtual.nome) ||
                             (plano.id === undefined && plano.nome === planoAtual.nome);
            const isDisabled = isCurrent || !plano.id;

            return (
              <PlanCard key={plano.id || plano.nome || `plano-${index}`} $isCurrent={isCurrent} $isDisabled={isDisabled}>
                <PlanHeader>
                  <PlanName $isCurrent={isCurrent}>{plano.nome?.toUpperCase() || 'PLANO'}</PlanName>
                  {isCurrent && (
                    <ActiveBadge>
                      <FiZap size={14} />
                      Ativo
                    </ActiveBadge>
                  )}
                </PlanHeader>

                <PlanDescription $isCurrent={isCurrent}>
                  {plano.descricao || 'Plano de serviços disponível'}
                </PlanDescription>

                <PlanDetails>
                  <DetailRow $isCurrent={isCurrent}>
                    <DetailLabel>Créditos Mensais:</DetailLabel>
                    <DetailValue $isCurrent={isCurrent}>{plano.creditos_mensais?.toLocaleString('pt-BR') || 0} créditos</DetailValue>
                  </DetailRow>
                  <DetailRow $isCurrent={isCurrent}>
                    <DetailLabel>Valor Mensal:</DetailLabel>
                    <DetailValue $isCurrent={isCurrent}>{formatCurrency(plano.valor || 0)}</DetailValue>
                  </DetailRow>
                </PlanDetails>

                {(() => {
                  const { recursosNovos, planoAnteriorNome } = calcularRecursosNovos(plano, todosPlanos);
                  const mostrarTodosRecursos = isCurrent || !planoAnteriorNome || recursosNovos.length === 0 || recursosNovos.length === plano.recursos?.length;
                  
                  if (!plano.recursos || plano.recursos.length === 0) {
                    return null;
                  }

                  return (
                    <PlanResources>
                      <ResourcesTitle $isCurrent={isCurrent}>Recursos Incluídos:</ResourcesTitle>
                      <ResourceList $isCurrent={isCurrent}>
                        {!mostrarTodosRecursos && planoAnteriorNome && (
                          <InheritedResourcesNote $isCurrent={isCurrent}>
                            Todos os recursos do plano {planoAnteriorNome}
                          </InheritedResourcesNote>
                        )}
                        {(mostrarTodosRecursos ? plano.recursos : recursosNovos).map((recurso) => (
                          <ResourceItem key={recurso.id || recurso.codigo} $isCurrent={isCurrent}>
                            <ResourceIcon $isCurrent={isCurrent}>
                              <FiCheck size={16} />
                            </ResourceIcon>
                            <ResourceContent>
                              <ResourceDescription $isCurrent={isCurrent}>
                                {recurso.descricao || 'Sem descrição'}
                              </ResourceDescription>
                            </ResourceContent>
                          </ResourceItem>
                        ))}
                      </ResourceList>
                    </PlanResources>
                  );
                })()}

                <UpgradeButton
                  $isCurrent={isCurrent}
                  $isDisabled={isDisabled || !isGestor()}
                  onClick={() => !isCurrent && !isDisabled && isGestor() && plano.id && abrirTelaSelecaoCartao(plano.id)}
                  disabled={isCurrent || isDisabled || !isGestor() || !plano.id}
                >
                  {isCurrent ? (
                    <>
                      <FiCheck />
                      Plano Atual
                    </>
                  ) : (
                    <>
                      Fazer Upgrade
                    </>
                  )}
                </UpgradeButton>
              </PlanCard>
            );
          })}
        </PlansGrid>
      </Container>
    </Layout>
  );
};

export default UpgradePlano;

